import Dropdown from "@shared/components/Dropdown/Dropdown";
import { useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import React, { useState } from "react";
import ConfirmationModal from "../../../components/shared/ConfirmationModal";

interface MessageItemProps {
  message: any;
  onDelete?: (messageId: string) => Promise<void>;
}

const MessageItem: React.FC<MessageItemProps> = ({ message, onDelete }) => {
  const { user } = useProfile();
  const isOwnMessage = message.senderId === user?.id;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (onDelete) {
      try {
        setIsDeleting(true);
        await onDelete(message.id);
      } catch (error) {
        console.error("Error deleting message:", error);
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirmation(false);
      }
    }
  };

  return (
    <>
      <div
        className={`flex items-start mb-4 ${
          isOwnMessage ? "justify-end" : "justify-start"
        } last:mb-0 mb-4 group`}
      >
        <div
          className={
            message.type === "text"
              ? "max-w-[75%]"
              : "max-w-[45%] md:max-w-[35%]"
          }
        >
          <div
            className={`relative p-3 rounded-lg ${
              isOwnMessage
                ? `${theme.bg.brand} ${theme.text.black} rounded-br-none`
                : "bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-bl-none"
            }`}
          >
            {/* Message options dropdown */}
            {isOwnMessage && (
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Dropdown
                  horizontalAlign="right"
                  verticalAlign="top"
                  className="z-50"
                  title="Message options"
                  items={[
                    {
                      id: "delete",
                      label: "Delete message",
                      onClick: () => setShowDeleteConfirmation(true),
                      icon: (
                        <svg
                          className={`fill-current ${theme.text.error} shrink-0`}
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                        >
                          <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
                        </svg>
                      ),
                    },
                  ]}
                  trigger={
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 11 7">
                      <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
                    </svg>
                  }
                />
              </div>
            )}

            {message.type === "deleted" && (
              <p className="italic">Message deleted</p>
            )}

            {message.type === "text" && <p>{message.content}</p>}

            {message.type === "image" && (
              <div>
                <img
                  src={message.mediaUrl}
                  alt="Shared image"
                  className="max-w-full rounded hover:opacity-90 transition-opacity"
                />
                {message.content && <p className="mt-2">{message.content}</p>}
              </div>
            )}

            {message.type === "video" && (
              <div>
                <video
                  src={message.mediaUrl}
                  controls
                  className="max-w-full rounded"
                />
                {message.content && <p className="mt-2">{message.content}</p>}
              </div>
            )}

            {message.type === "gif" && (
              <div>
                <img
                  src={message.mediaUrl}
                  alt="GIF"
                  className="max-w-full rounded"
                />
                {message.content && <p className="mt-2">{message.content}</p>}
              </div>
            )}

            {message.type === "audio" && (
              <div>
                <audio src={message.mediaUrl} controls className="max-w-full" />
                {message.content && <p className="mt-2">{message.content}</p>}
              </div>
            )}

            {message.type === "file" && (
              <div className="flex items-center">
                <svg
                  className="w-8 h-8 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
                </svg>
                <a
                  href={message.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-violet-400 transition-colors"
                >
                  {message.content || "Download file"}
                </a>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className={`text-xs ${theme.text.muted}`}>
              {new Date(message.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
            {isOwnMessage &&
              (message.read ? (
                <svg
                  className={`w-5 h-3 shrink-0 fill-current ${theme.text.success}`}
                  viewBox="0 0 20 12"
                >
                  <path d="M10.402 6.988l1.586 1.586L18.28 2.28a1 1 0 011.414 1.414l-7 7a1 1 0 01-1.414 0L8.988 8.402l-2.293 2.293a1 1 0 01-1.414 0l-3-3A1 1 0 013.695 6.28l2.293 2.293L12.28 2.28a1 1 0 011.414 1.414l-3.293 3.293z" />
                </svg>
              ) : (
                <svg
                  className="w-3 h-3 shrink-0 fill-current text-gray-400"
                  viewBox="0 0 12 12"
                >
                  <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                </svg>
              ))}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete Message"
        message="Are you sure you want to delete this message? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default MessageItem;
